import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CustomerService } from "services/CustomerService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { GetCustomerDashboardDto } from "services/Dtos/GetCustomerDashboardDto";
import { GetApiKeyDto, PostApiKeyDto } from "services/Dtos/ApiKeyDtos";
import { PostApiKeyConfigDto } from "services/Dtos/ApiKeyDtos";
import Breadcrumb from "../common/Breadcrumb";
import ApiKeyDetailsTab from "./Tabs/ApiKey/ApiKeyDetailsTab";
import ApiKeyOverviewTab from "./Tabs/ApiKey/ApiKeyOverviewTab";
import ApiKeyConfigurationTab from "./Tabs/ApiKey/ApiKeyConfigurationTab";
import ApiKeyEditTab from "./Tabs/ApiKey/ApiKeyEditTab";

type TabType = 'details' | 'overview' | 'configuration' | 'edit';

const generateSuggestedApiKey = (): string => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const generatePart = () =>
    Array(5)
      .fill(0)
      .map(() =>
        characters.charAt(Math.floor(Math.random() * characters.length))
      )
      .join("");
  const part1 = generatePart();
  const part2 = generatePart();
  const part3 = generatePart();
  return `${part1}-${part2}-${part3}`;
};

const isValidApiKeyFormat = (key: string): boolean => {
  const apiKeyRegex = /^.{5}-.{5}-.{5}$/;
  return apiKeyRegex.test(key);
};

const isValidUSPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^(?:\(\d{3}\)\s?\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{10})$/;
  return phoneRegex.test(phone);
};

const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length !== 10) return phone;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

const ApiKeyDetails: React.FC = () => {
  const { id, apiKeyId } = useParams<{ id: string; apiKeyId: string }>();
  const [customer, setCustomer] = useState<GetCustomerDashboardDto | null>(null);
  const [apiKey, setApiKey] = useState<GetApiKeyDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Configuration tab state
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [isConfigLoading, setIsConfigLoading] = useState(false);

  // Edit tab state
  const [formData, setFormData] = useState({
    name: "",
    key: "",
    phone: "",
    email: "",
    description: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isEditLoading, setIsEditLoading] = useState(false);

  const [togglingGemini, setTogglingGemini] = useState(false);

  const fetchCustomerAndApiKey = async () => {
    try {
      setIsLoading(true);
      const response = await CustomerService.customer.getCustomer(parseInt(id || ""));
      setCustomer(response.data || null);

      const foundApiKey = response.data?.apiKeys.find(
        (key) => key.id === parseInt(apiKeyId || "")
      );
      setApiKey(foundApiKey || null);

      if (!foundApiKey) {
        setError("API Key not found");
      } else {
        // Initialize form data with API key values
        setFormData({
          name: foundApiKey.name,
          key: foundApiKey.key,
          phone: foundApiKey.phone || "",
          email: foundApiKey.email || "",
          description: foundApiKey.description || "",
        });
      }
    } catch (err) {
      setError("Failed to load API key details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id && apiKeyId) {
      fetchCustomerAndApiKey();
    }
  }, [id, apiKeyId]);

  // Load configuration fields when configuration tab is active
  useEffect(() => {
    const loadCurrentConfigs = async () => {
      if (activeTab !== 'configuration' || !apiKey) return;

      try {
        setIsConfigLoading(true);
        const response = await CustomerService.apiKey.getApiKeyConfigs(
          parseInt(id || ""),
          apiKey.id
        );
        const configuredFields = response.data.map((config) => config.extraField);
        setSelectedFields(configuredFields);
      } catch (err) {
        toast.error("Failed to load configurations", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsConfigLoading(false);
      }
    };

    loadCurrentConfigs();
  }, [activeTab, apiKey, id]);

  const handleToggleGemini = async () => {
    if (!apiKey) return;

    setTogglingGemini(true);
    try {
      await CustomerService.apiKey.toggleGemini(apiKey.id);
      toast.success(`Gemini setting updated for ${apiKey.name}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      fetchCustomerAndApiKey();
    } catch (err) {
      console.error("Failed to toggle Gemini:", err);
      toast.error("Failed to update Gemini setting", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setTogglingGemini(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  // Configuration handlers
  const handleCheckboxChange = (propertyName: string) => {
    setSelectedFields((prev) => {
      if (prev.includes(propertyName)) {
        return prev.filter((field) => field !== propertyName);
      }
      return [...prev, propertyName];
    });
  };

  const handleConfigSave = async () => {
    if (!apiKey) return;

    try {
      setIsConfigLoading(true);
      const configs: PostApiKeyConfigDto[] = selectedFields.map((field) => ({
        extraField: field,
        extraFieldType: "Address",
      }));

      await CustomerService.apiKey.updateApiKeyConfigs(
        parseInt(id || ""),
        apiKey.id,
        configs
      );

      toast.success("Configuration saved successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      toast.error("Failed to save configurations", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsConfigLoading(false);
    }
  };

  // Edit handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "phone") {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        return null;
      case "key":
        if (!value.trim()) return "API Key is required";
        if (!isValidApiKeyFormat(value))
          return "API Key must be in format XXXXX-XXXXX-XXXXX";
        return null;
      case "phone":
        if (value && !isValidUSPhoneNumber(value))
          return "Phone must be a valid US phone number";
        return null;
      case "email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Email must be valid";
        return null;
      default:
        return null;
    }
  };

  const handleEditSave = async () => {
    if (!apiKey) return;

    const newErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      setIsEditLoading(true);
      const updateData: PostApiKeyDto = {
        name: formData.name,
        key: formData.key,
        phone: formData.phone || null,
        email: formData.email || null,
        description: formData.description || null,
        customerId: parseInt(id || ""),
      };

      await CustomerService.apiKey.updateApiKey(apiKey.id, updateData);

      toast.success("API Key updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      fetchCustomerAndApiKey();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to update API key";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleGenerateKey = () => {
    const newKey = generateSuggestedApiKey();
    setFormData((prev) => ({ ...prev, key: newKey }));
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !customer || !apiKey) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger" role="alert">
          {error || "API Key not found"}
        </div>
        <Link to={`/customers/${id}/api-keys`} className="btn btn-primary">
          <i className="bi bi-arrow-left me-2"></i>
          Back to API Keys
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <ToastContainer />

      <Breadcrumb
        items={[
          { label: 'Customers', path: '/customers' },
          { label: customer.name, path: `/customers/${id}` },
          { label: 'API Keys', path: `/customers/${id}` },
          { label: apiKey.key }
        ]}
      />

      {/* Tabs Navigation */}
      <div className="row">
        <div className="col-md-2">
          <div className="nav flex-column nav-pills sticky-top" style={{ top: '20px' }}>
            <button
              className={`nav-link text-start ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => handleTabChange('details')}
            >
              <i className="bi bi-info-circle me-2"></i>
              Details
            </button>
            <button
              className={`nav-link text-start ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => handleTabChange('overview')}
            >
              <i className="bi bi-graph-up me-2"></i>
              Overview
            </button>
            <button
              className={`nav-link text-start ${activeTab === 'configuration' ? 'active' : ''}`}
              onClick={() => handleTabChange('configuration')}
            >
              <i className="bi bi-gear me-2"></i>
              Configuration
            </button>
            <button
              className={`nav-link text-start ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => handleTabChange('edit')}
            >
              <i className="bi bi-pencil me-2"></i>
              Edit API Key
            </button>
          </div>
        </div>

        <div className="col-md-10">
          {activeTab === 'details' && (
            <ApiKeyDetailsTab
              apiKey={apiKey}
              onToggleGemini={handleToggleGemini}
              togglingGemini={togglingGemini}
            />
          )}

          {activeTab === 'overview' && (
            <ApiKeyOverviewTab apiKeyId={parseInt(apiKeyId || '0')} />
          )}

          {activeTab === 'configuration' && (
            <ApiKeyConfigurationTab
              selectedFields={selectedFields}
              onCheckboxChange={handleCheckboxChange}
              onSave={handleConfigSave}
              isLoading={isConfigLoading}
            />
          )}

          {activeTab === 'edit' && (
            <ApiKeyEditTab
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
              onGenerateKey={handleGenerateKey}
              onSave={handleEditSave}
              isLoading={isEditLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeyDetails;
